/**
 * §7.4 號碼池。
 *
 * §12.2：PV 號的格式 `PVR-01-2634-000481-7` 裡的 `2634` 本來就是場站代碼，
 * 所以各廠區的號碼天然不衝突（§12.7）。
 * 檢查碼是 Mod-11，餘數為 10 的序號在產號時直接跳過。
 */

import { useState } from 'react'
import { api } from '../api/client.js'
import { useMutation, useQuery } from '../api/hooks.js'
import { useAuth } from '../auth/AuthContext.js'
import { Alert, Button, Card, Empty, Field, Mono, Pill, Select, Table, Td, TextInput } from '../components/ui.js'
import { count, ts } from '../../../shared/lib/format.js'

interface Pool {
  idType: 'panel' | 'item'
  counts: { total: number; available: number; reserved: number; used: number; voided: number }
  ids: { id: string; status: string; reservation_id: string | null }[]
  reservations: {
    reservation_id: string; reserved_by: string; device_id: string
    reserved_at: string; status: string; total: string; unused: string
  }[]
}

export function IdPool() {
  const { can } = useAuth()
  const [idType, setIdType] = useState<'panel' | 'item'>('panel')
  const pool = useQuery<Pool>(`/api/id-pool?idType=${idType}&limit=200`)
  const [genCount, setGenCount] = useState('2000')
  const [rsvCount, setRsvCount] = useState('200')
  const [deviceId, setDeviceId] = useState('RCV-TABLET-01')
  const [notice, setNotice] = useState<string | null>(null)

  const gen = useMutation<void, { count: number; first: string; last: string }>(async (_a, key) =>
    api.post('/api/id-pool/generate', { idType, count: Number(genCount) }, { idempotencyKey: key }))
  const rsv = useMutation<void, { reservationId: string; ids: string[]; shortfall: number }>(async (_a, key) =>
    api.post('/api/id-pool/reserve', { idType, count: Number(rsvCount), deviceId }, { idempotencyKey: key }))
  const reclaim = useMutation<{ reservationId: string; reason: string }, { reclaimed: number; alreadyUsed: number }>(
    async (a, key) => api.post('/api/id-pool/reclaim', a, { idempotencyKey: key }))

  const c = pool.data?.counts

  return (
    <div className="space-y-4">
      {notice && <Alert tone="ok">{notice}</Alert>}

      <Card
        title="號碼池"
        subtitle="PV 號的前綴含場站代碼，所以各廠區的號碼天然不衝突"
        right={
          <Select value={idType} onChange={(e) => setIdType(e.target.value as 'panel' | 'item')}>
            <option value="panel">PV 號</option>
            <option value="item">料件號</option>
          </Select>
        }
      >
        {c && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {([['總數', c.total, 'ink'], ['可用', c.available, 'emerald'], ['預提中', c.reserved, 'sky'],
               ['已使用', c.used, 'ink'], ['作廢', c.voided, 'red']] as const).map(([label, v, tone]) => (
              <div key={label} className="rounded-lg border border-ink-200 bg-ink-50 p-3">
                <p className="text-xs text-ink-500">{label}</p>
                <p className="mt-1 text-xl font-semibold tabular-nums">{count(v)}</p>
                {v > 0 && tone !== 'ink' && <Pill tone={tone}>{label}</Pill>}
              </div>
            ))}
          </div>
        )}
        {c && c.available === 0 && (
          <div className="mt-3">
            <Alert tone="warn" title="沒有可用號碼">
              號碼池空了，現場無法建檔。§12.3.3 的完成度檢查第 6 項也會不通過。請先產生一批。
            </Alert>
          </div>
        )}
      </Card>

      {can('idpool.generate') && (
        <Card title="產生號碼" subtitle="接在目前最大號之後，檢查碼用 Mod-11">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="要產生幾組">
              <TextInput inputMode="numeric" value={genCount} className="w-[140px]"
                onChange={(e) => setGenCount(e.target.value.replace(/\D/g, ''))} />
            </Field>
            <Button variant="primary" size="lg" disabled={!genCount || gen.pending}
              onClick={async () => {
                const r = await gen.run()
                if (r) { setNotice(`已產生 ${count(r.count)} 組：${r.first} … ${r.last}`); pool.reload() }
              }}>{gen.pending ? '產生中…' : '產生'}</Button>
          </div>
          {gen.error && <div className="mt-3"><Alert tone="danger">{gen.error.message}</Alert></div>}
        </Card>
      )}

      {can('idpool.reserve') && (
        <Card title="預提號碼" subtitle="把號碼交給某台裝置，現場拿去印標籤與建檔">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="要預提幾組">
              <TextInput inputMode="numeric" value={rsvCount} className="w-[140px]"
                onChange={(e) => setRsvCount(e.target.value.replace(/\D/g, ''))} />
            </Field>
            <Field label="裝置">
              <TextInput value={deviceId} className="w-[200px]"
                onChange={(e) => setDeviceId(e.target.value)} />
            </Field>
            <Button variant="primary" size="lg" disabled={!rsvCount || rsv.pending}
              onClick={async () => {
                const r = await rsv.run()
                if (r) {
                  setNotice(`已預提 ${count(r.ids.length)} 組（${r.reservationId}）` +
                    (r.shortfall > 0 ? `——比要求的少 ${r.shortfall} 組，池子不夠了` : ''))
                  pool.reload()
                }
              }}>{rsv.pending ? '預提中…' : '預提'}</Button>
          </div>
          {rsv.error && <div className="mt-3"><Alert tone="danger">{rsv.error.message}</Alert></div>}
        </Card>
      )}

      <Card title="預提紀錄">
        {(pool.data?.reservations ?? []).length === 0 ? <Empty>還沒有預提紀錄。</Empty> : (
          <Table head={['預提單', '裝置', '數量', '未使用', '時間', '狀態', '']} dense>
            {pool.data!.reservations.map((r) => (
              <tr key={r.reservation_id}>
                <Td><Mono>{r.reservation_id}</Mono></Td>
                <Td className="text-xs">{r.device_id}</Td>
                <Td className="tabular-nums text-xs">{r.total}</Td>
                <Td className="tabular-nums text-xs">{r.unused}</Td>
                <Td className="text-xs">{ts(r.reserved_at)}</Td>
                <Td><Pill tone={r.status === 'active' ? 'sky' : 'ink'}>{
                  r.status === 'active' ? '有效' : r.status === 'reclaimed' ? '已回收' : r.status
                }</Pill></Td>
                <Td>
                  {can('idpool.reclaim') && r.status === 'active' && Number(r.unused) > 0 && (
                    <Button size="sm" onClick={async () => {
                      const reason = prompt('回收原因？（例：標籤印壞、當班沒用完）')
                      if (!reason) return
                      const out = await reclaim.run({ reservationId: r.reservation_id, reason })
                      if (out) {
                        setNotice(`已回收 ${count(out.reclaimed)} 組` +
                          (out.alreadyUsed > 0 ? `；${out.alreadyUsed} 組已經用掉了，收不回來` : ''))
                        pool.reload()
                      }
                    }}>回收未用的</Button>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
        {reclaim.error && <div className="mt-3"><Alert tone="danger">{reclaim.error.message}</Alert></div>}
      </Card>

      <Card title="號碼明細" subtitle="可用與預提中的前 200 組">
        {(pool.data?.ids ?? []).length === 0 ? <Empty>—</Empty> : (
          <div className="max-h-72 overflow-y-auto">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-x-3 gap-y-0.5 text-xs">
              {pool.data!.ids.map((x) => (
                <div key={x.id} className="flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${
                    x.status === 'available' ? 'bg-emerald-500' : 'bg-sky-500'}`} />
                  <Mono>{x.id}</Mono>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
